import {forwardRef, useCallback, useEffect, useRef, useState, type KeyboardEvent} from 'react';
import {ArrowDownLeft, ArrowDownRight, ArrowUpLeft, ArrowUpRight, GripVertical, Phone} from 'lucide-react';
import {useIntl, type MessageDescriptor} from 'react-intl';
import {callSurfaceMessages as m} from '../messages';
import {callDock, cornerDock, useCallDock, useDockStyle, type DockAnchor} from './callDock';
import {clampToViewport, dockAt, useDockDrag} from './useDockDrag';

const PILL_SIZE = {width: 176, height: 36};
const KEY_STEP = 16;
const KEY_STEP_LARGE = 64;

const KEY_MOVES: Record<string, readonly [number, number]> = {
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0],
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
};

const CORNERS: readonly {
    readonly horizontal: DockAnchor['horizontal'];
    readonly vertical: DockAnchor['vertical'];
    readonly label: MessageDescriptor;
    readonly Icon: typeof ArrowUpLeft;
}[] = [
    {horizontal: 'left', vertical: 'top', label: m.pillCornerTopLeft, Icon: ArrowUpLeft},
    {horizontal: 'right', vertical: 'top', label: m.pillCornerTopRight, Icon: ArrowUpRight},
    {horizontal: 'left', vertical: 'bottom', label: m.pillCornerBottomLeft, Icon: ArrowDownLeft},
    {horizontal: 'right', vertical: 'bottom', label: m.pillCornerBottomRight, Icon: ArrowDownRight},
];

const FOCUS_RING = 'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--phq-call-brand-focus)]';

const CornerMenu = ({anchor, onPick, onClose}: {anchor: DockAnchor; onPick: (anchor: DockAnchor) => void; onClose: (restoreFocus: boolean) => void}) => {
    const intl = useIntl();
    const menu = useRef<HTMLDivElement>(null);

    useEffect(() => {
        menu.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
        const onPointerDown = (event: globalThis.PointerEvent) => {
            if (!menu.current?.parentElement?.contains(event.target as Node)) {
                onClose(false);
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [onClose]);

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const items = [...(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? [])];
        const index = items.findIndex(item => item === document.activeElement);
        if (event.key === 'Escape') {
            event.preventDefault();
            onClose(true);
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            items[(index + step + items.length) % items.length]?.focus();
        }
    };

    return (
        <div
            ref={menu}
            role="menu"
            aria-label={intl.formatMessage(m.pillCorners)}
            data-testid="start-call-pill-corners"
            onKeyDown={onKeyDown}
            className={`phq-call-swap absolute flex w-44 flex-col gap-0.5 rounded-xl border border-solid border-white/15 bg-[var(--phq-call-canvas)] p-1.5 phq-call-shadow-menu ${anchor.vertical === 'bottom' ? 'bottom-[calc(100%+8px)]' : 'top-[calc(100%+8px)]'} ${anchor.horizontal === 'right' ? 'right-0' : 'left-0'}`}
        >
            {CORNERS.map(({horizontal, vertical, label, Icon}) => {
                const current = anchor.horizontal === horizontal && anchor.vertical === vertical;
                return (
                    <button
                        key={`${vertical}-${horizontal}`}
                        type="button"
                        role="menuitemradio"
                        aria-checked={current}
                        onClick={() => onPick(cornerDock(horizontal, vertical))}
                        className={`inline-flex h-8 cursor-pointer items-center gap-2 rounded-lg px-2.5 text-[13px] font-medium hover:bg-white/10 ${current ? 'text-[var(--phq-call-brand-focus)]' : 'text-white/90'} ${FOCUS_RING}`}
                    >
                        <Icon size={14} aria-hidden="true"/>
                        {intl.formatMessage(label)}
                    </button>
                );
            })}
        </div>
    );
};

export const StartCallPill = forwardRef<HTMLButtonElement, {onOpen: () => void}>(({onOpen}, ref) => {
    const intl = useIntl();
    const anchor = useCallDock();
    const docked = useDockStyle(PILL_SIZE);
    const container = useRef<HTMLDivElement>(null);
    const handle = useRef<HTMLButtonElement>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const {position, onPointerDown, onPointerMove, onPointerEnd, consumeClick} = useDockDrag(container, () => setMenuOpen(false));

    const closeMenu = useCallback((restoreFocus: boolean) => {
        setMenuOpen(false);
        if (restoreFocus) {
            handle.current?.focus();
        }
    }, []);

    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        const move = KEY_MOVES[event.key];
        const rect = container.current?.getBoundingClientRect();
        if (!move || !rect || menuOpen) {
            return;
        }
        event.preventDefault();
        const step = event.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
        dockAt(clampToViewport(rect.left + move[0] * step, rect.top + move[1] * step, rect.width, rect.height), rect.width, rect.height);
    };

    return (
        <div
            ref={container}
            data-testid="start-call-dock"
            style={position ? {left: position.left, top: position.top} : docked}
            className={`phq-call-ui fixed z-[60] ${position ? '' : 'phq-call-enter'}`}
        >
            <div className="inline-flex h-9 select-none items-stretch overflow-hidden rounded-2xl border border-solid border-white/30 bg-[var(--phq-call-canvas)] text-[13px] font-medium text-white/90 shadow-[0_1px_2px_rgba(0,0,0,.17),0_3px_3px_rgba(0,0,0,.15),0_7px_4px_rgba(0,0,0,.09),0_13px_5px_rgba(0,0,0,.03)]">
                <button
                    ref={handle}
                    type="button"
                    data-testid="start-call-pill-handle"
                    aria-label={intl.formatMessage(m.pillMove)}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    title={intl.formatMessage(m.pillDragHint)}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerEnd}
                    onPointerCancel={onPointerEnd}
                    onKeyDown={onKeyDown}
                    onClick={() => {
                        if (consumeClick()) {
                            return;
                        }
                        setMenuOpen(open => !open);
                    }}
                    className={`inline-flex w-7 shrink-0 touch-none items-center justify-center border-0 border-r border-solid border-white/15 transition-colors hover:bg-white/10 hover:text-white/85 ${position || menuOpen ? 'bg-white/10 text-white/85' : 'text-white/40'} ${position ? 'cursor-grabbing' : 'cursor-grab'} ${FOCUS_RING}`}
                >
                    <GripVertical size={14} aria-hidden="true"/>
                </button>
                <button
                    ref={ref}
                    type="button"
                    data-testid="start-call-pill"
                    aria-label={intl.formatMessage(m.pillAria)}
                    onClick={onOpen}
                    className={`phq-call-press inline-flex cursor-pointer items-center gap-1.5 pl-2.5 pr-3.5 transition-colors hover:bg-white/10 ${FOCUS_RING}`}
                >
                    <Phone size={14} strokeWidth={2} className="text-white/60" aria-hidden="true"/>
                    <span>{intl.formatMessage(m.pillLabel)}</span>
                </button>
            </div>
            {menuOpen && !position && (
                <CornerMenu
                    anchor={anchor}
                    onClose={closeMenu}
                    onPick={next => {
                        callDock.set(next);
                        closeMenu(true);
                    }}
                />
            )}
        </div>
    );
});

StartCallPill.displayName = 'StartCallPill';
